import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiPencilSimpleBold,
    PiEnvelopeBold,
    PiPhoneBold,
    PiMapPinBold,
    PiUserBold,
    PiAnchorBold,
    PiCheckCircleBold,
    PiXCircleBold,
    PiClockBold,
    PiCalendarBold,
    PiTrashBold,
    PiWarningCircleBold,
    PiArrowSquareOutBold,
    PiNotepadBold,
    PiLinkBold,
    PiBuildingsBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { PageHeader } from '@v2/design-system';
import { cn } from '@v2/utils/cn';

/* ─── Types ─── */
type BrokerDetail = {
    id: number;
    name: string;
    cnpj: string | null;
    contact_person: string | null;
    phone: string | null;
    email: string | null;
    email_secondary: string | null;
    address: string | null;
    notes: string | null;
    active: boolean;
    stats: {
        total_shipments: number;
        open_shipments: number;
        done_shipments: number;
        total_emails: number;
        last_email_at: string | null;
        last_shipment_at: string | null;
    };
    shipments: {
        id: number;
        order_number: string | null;
        invoice: string | null;
        supplier: string | null;
        stage: string;
        process_status: string;
        order_date: string | null;
        customs_clearance_date: string | null;
        eta_destination: string | null;
        vessel_name: string | null;
    }[];
    email_logs: {
        id: number;
        to_email: string;
        cc_email: string | null;
        subject: string;
        body: string | null;
        type: string;
        status: string;
        attachments: string[];
        sent_at: string | null;
        user_name: string | null;
        shipment_id: number | null;
        shipment_number: string | null;
    }[];
    timeline: {
        id: string;
        type: string;
        date: string;
        label?: string;
        subject?: string;
        to_email?: string;
        status?: string;
        user?: string | null;
        shipment_id?: number | null;
        shipment_number?: string | null;
        supplier?: string | null;
    }[];
};

/* ─── Helpers ─── */
const fmtDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const STAGE_LABELS: Record<string, string> = {
    pedido: 'Pedido', validacao: 'Validação', financeiro: 'Financeiro',
    producao: 'Produção', financeiro2: 'Fin. 2', embarque: 'Embarque',
    transito: 'Trânsito', desembaraco: 'Desembaraço', fiscal: 'Fiscal',
    entrega: 'Entrega', concluido: 'Concluído',
};

/* ─── Stat card ─── */
function StatCard({ icon, label, value, color }: {
    icon: React.ReactNode; label: string; value: number | string; color: string;
}) {
    return (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-muted bg-background p-5 shadow-sm">
            <div className={cn('mb-2 flex h-12 w-12 items-center justify-center rounded-xl text-white', color)}>
                {icon}
            </div>
            <p className="text-2xl font-bold text-foreground">{value}</p>
            <p className="mt-0.5 text-xs text-muted-foreground text-center">{label}</p>
        </div>
    );
}

/* ─── Info Row ─── */
function InfoRow({ icon, label, value, href }: {
    icon: React.ReactNode; label: string; value: string | null | undefined; href?: string;
}) {
    if (!value) return null;
    const content = (
        <span className="text-sm text-foreground">{value}</span>
    );
    return (
        <div className="flex items-start gap-3 py-2.5 border-b border-muted last:border-0">
            <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
            <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                {href ? <a href={href} className="text-sm text-primary hover:underline">{value}</a> : content}
            </div>
        </div>
    );
}

/* ─── Timeline Item ─── */
function TimelineItem({ item }: { item: BrokerDetail['timeline'][number] }) {
    const isEmail = item.type === 'email';
    const isClearance = item.type === 'clearance';
    return (
        <div className="flex gap-4">
            <div className="flex flex-col items-center">
                <div className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full shrink-0',
                    isEmail ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                    isClearance ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                    'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
                )}>
                    {isEmail ? <PiEnvelopeBold className="h-4 w-4" /> :
                     isClearance ? <PiCheckCircleBold className="h-4 w-4" /> :
                     <PiAnchorBold className="h-4 w-4" />}
                </div>
                <div className="w-px flex-1 bg-muted mt-1" />
            </div>
            <div className="pb-5 min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground leading-tight">
                            {isEmail ? item.subject : item.label}
                        </p>
                        {isEmail && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Para: <strong>{item.to_email}</strong>
                                {item.user && <> · por {item.user}</>}
                            </p>
                        )}
                        {item.shipment_number && (
                            <Link
                                to={`/shipments/${item.shipment_id}`}
                                className="mt-0.5 text-xs text-primary flex items-center gap-1 hover:underline"
                            >
                                <PiLinkBold className="h-3 w-3" />
                                Embarque #{item.shipment_number}
                            </Link>
                        )}
                        {item.supplier && (
                            <p className="text-xs text-muted-foreground mt-0.5">{item.supplier}</p>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-nowrap shrink-0 pt-0.5">{fmtDate(item.date)}</p>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ─── */
export default function CustomsBrokerDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [tab, setTab] = useState<'timeline' | 'shipments' | 'emails'>('timeline');
    const qc = useQueryClient();

    const { data, isLoading, isError } = useQuery({
        queryKey: ['broker-detail', id],
        queryFn: async () => {
            const { data } = await api.get<{ data: BrokerDetail }>(`/customs-brokers/${id}/detail`);
            return data.data;
        },
        enabled: !!id,
    });

    const deleteEmail = useMutation({
        mutationFn: (logId: number) => api.delete(`/customs-brokers/${id}/email-logs/${logId}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['broker-detail', id] }),
    });

    const tabCls = (t: typeof tab) => cn(
        'px-5 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap',
        tab === t
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground',
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-3">
                <PiWarningCircleBold className="h-14 w-14 text-muted-foreground/40" />
                <p className="text-base font-medium text-muted-foreground">Despachante não encontrado</p>
                <Link to="/settings/customs-brokers" className="text-sm text-primary hover:underline flex items-center gap-1">
                    <PiArrowLeftBold className="h-4 w-4" /> Voltar para despachantes
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={data.name}
                breadcrumb={[
                    { name: 'Configurações', href: '/settings/customs-brokers' },
                    { name: 'Despachantes', href: '/settings/customs-brokers' },
                    { name: data.name },
                ]}
            >
                <Link
                    to="/settings/customs-brokers"
                    className="flex items-center gap-2 rounded-xl border border-muted px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                >
                    <PiArrowLeftBold className="h-4 w-4" />
                    Voltar
                </Link>
            </PageHeader>

            {/* Hero + KPIs */}
            <div className="grid gap-6 lg:grid-cols-3">

                {/* Perfil */}
                <div className="rounded-2xl border border-muted bg-background shadow-sm p-6">
                    {/* Avatar */}
                    <div className="flex items-center gap-4 mb-5">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 shrink-0">
                            <PiBuildingsBold className="h-8 w-8" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-foreground leading-tight">{data.name}</h2>
                            {data.cnpj && (
                                <p className="text-sm text-muted-foreground font-mono mt-0.5">{data.cnpj}</p>
                            )}
                            <span className={cn(
                                'mt-1.5 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                                data.active
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            )}>
                                {data.active ? <PiCheckCircleBold className="h-3 w-3" /> : <PiXCircleBold className="h-3 w-3" />}
                                {data.active ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>

                    {/* Dados de contato */}
                    <div className="space-y-0">
                        <InfoRow icon={<PiUserBold className="h-4 w-4" />} label="Responsável" value={data.contact_person} />
                        <InfoRow icon={<PiPhoneBold className="h-4 w-4" />} label="Telefone" value={data.phone} href={data.phone ? `tel:${data.phone}` : undefined} />
                        <InfoRow icon={<PiEnvelopeBold className="h-4 w-4" />} label="E-mail principal" value={data.email} href={data.email ? `mailto:${data.email}` : undefined} />
                        <InfoRow icon={<PiEnvelopeBold className="h-4 w-4 opacity-60" />} label="E-mail secundário" value={data.email_secondary} href={data.email_secondary ? `mailto:${data.email_secondary}` : undefined} />
                        <InfoRow icon={<PiMapPinBold className="h-4 w-4" />} label="Endereço" value={data.address} />
                    </div>

                    {data.notes && (
                        <div className="mt-4 rounded-xl bg-muted/30 p-3">
                            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-1">
                                <PiNotepadBold className="h-3.5 w-3.5" />
                                Observações
                            </p>
                            <p className="text-sm text-foreground leading-relaxed">{data.notes}</p>
                        </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-muted">
                        <Link
                            to={`/settings/customs-brokers/${id}/edit`}
                            className="flex items-center justify-center gap-2 w-full rounded-xl border border-muted px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                        >
                            <PiPencilSimpleBold className="h-4 w-4" />
                            Editar dados
                        </Link>
                    </div>
                </div>

                {/* KPI cards */}
                <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
                    <StatCard
                        icon={<PiAnchorBold className="h-6 w-6" />}
                        label="Total de embarques"
                        value={data.stats.total_shipments}
                        color="bg-amber-500"
                    />
                    <StatCard
                        icon={<PiClockBold className="h-6 w-6" />}
                        label="Embarques em aberto"
                        value={data.stats.open_shipments}
                        color="bg-blue-500"
                    />
                    <StatCard
                        icon={<PiCheckCircleBold className="h-6 w-6" />}
                        label="Embarques finalizados"
                        value={data.stats.done_shipments}
                        color="bg-green-500"
                    />
                    <StatCard
                        icon={<PiEnvelopeBold className="h-6 w-6" />}
                        label="E-mails registrados"
                        value={data.stats.total_emails}
                        color="bg-purple-500"
                    />
                    {data.stats.last_shipment_at && (
                        <div className="col-span-2 rounded-2xl border border-muted bg-background p-4 flex items-center gap-3">
                            <PiCalendarBold className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-xs text-muted-foreground">Último embarque</p>
                                <p className="text-sm font-semibold text-foreground">{fmtDate(data.stats.last_shipment_at)}</p>
                            </div>
                            {data.stats.last_email_at && (
                                <>
                                    <div className="w-px h-8 bg-muted mx-2" />
                                    <PiEnvelopeBold className="h-5 w-5 text-muted-foreground shrink-0" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Último e-mail</p>
                                        <p className="text-sm font-semibold text-foreground">{fmtDate(data.stats.last_email_at)}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="rounded-2xl border border-muted bg-background shadow-sm overflow-hidden">
                <div className="flex items-center gap-0 border-b border-muted overflow-x-auto">
                    <button className={tabCls('timeline')} onClick={() => setTab('timeline')}>
                        Linha do Tempo
                    </button>
                    <button className={tabCls('shipments')} onClick={() => setTab('shipments')}>
                        Embarques ({data.stats.total_shipments})
                    </button>
                    <button className={tabCls('emails')} onClick={() => setTab('emails')}>
                        E-mails ({data.stats.total_emails})
                    </button>
                </div>

                <div className="p-6">

                    {/* TIMELINE */}
                    {tab === 'timeline' && (
                        data.timeline.length === 0 ? (
                            <div className="flex flex-col items-center py-16 text-center">
                                <PiCalendarBold className="h-14 w-14 text-muted-foreground/30 mb-4" />
                                <p className="text-base font-medium text-muted-foreground">Nenhuma atividade ainda</p>
                                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                                    Vincule embarques a este despachante para construir o histórico de atividades.
                                </p>
                            </div>
                        ) : (
                            <div className="max-w-2xl">
                                {data.timeline.map((item) => (
                                    <TimelineItem key={item.id} item={item} />
                                ))}
                            </div>
                        )
                    )}

                    {/* SHIPMENTS */}
                    {tab === 'shipments' && (
                        data.shipments.length === 0 ? (
                            <div className="flex flex-col items-center py-16 text-center">
                                <PiAnchorBold className="h-14 w-14 text-muted-foreground/30 mb-4" />
                                <p className="text-base font-medium text-muted-foreground">Nenhum embarque vinculado</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Para vincular, edite um embarque e selecione este despachante no campo correspondente.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {data.shipments.map((ship) => (
                                    <div key={ship.id} className="flex items-center justify-between rounded-xl border border-muted bg-background p-4 hover:bg-muted/20 transition-colors">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-semibold text-sm text-foreground">
                                                    {ship.order_number ?? ship.invoice ?? `Embarque #${ship.id}`}
                                                </span>
                                                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                                    {STAGE_LABELS[ship.stage] ?? ship.stage}
                                                </span>
                                                <span className={cn(
                                                    'rounded-full px-2 py-0.5 text-xs font-medium',
                                                    ship.process_status === 'Finalizado'
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                                                )}>
                                                    {ship.process_status}
                                                </span>
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                                                {ship.supplier && <span>{ship.supplier}</span>}
                                                {ship.vessel_name && <span>Navio: {ship.vessel_name}</span>}
                                                {ship.order_date && <span>Pedido: {fmtDate(ship.order_date)}</span>}
                                                {ship.eta_destination && <span>ETA: {fmtDate(ship.eta_destination)}</span>}
                                                {ship.customs_clearance_date && (
                                                    <span className="text-green-600 dark:text-green-400">
                                                        ✓ Desembaraço: {fmtDate(ship.customs_clearance_date)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <a
                                            href={`/shipments/${ship.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-4 p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                                            title="Abrir embarque"
                                        >
                                            <PiArrowSquareOutBold className="h-4 w-4" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {/* EMAILS */}
                    {tab === 'emails' && (
                        <div>
                            <div className="mb-5">
                                <h3 className="text-sm font-semibold text-foreground">
                                    {data.email_logs.length} e-mail{data.email_logs.length !== 1 ? 's' : ''} registrado{data.email_logs.length !== 1 ? 's' : ''}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">E-mails são registrados automaticamente ao executar tarefas de desembaraço.</p>
                            </div>

                            {data.email_logs.length === 0 ? (
                                <div className="flex flex-col items-center py-16 text-center">
                                    <PiEnvelopeBold className="h-14 w-14 text-muted-foreground/30 mb-4" />
                                    <p className="text-base font-medium text-muted-foreground">Nenhum e-mail registrado</p>
                                    <p className="text-sm text-muted-foreground mt-1">Os e-mails enviados pelas tarefas aparecerão aqui.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {data.email_logs.map((log) => (
                                        <div key={log.id} className="rounded-xl border border-muted bg-background p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-semibold text-sm text-foreground">{log.subject}</p>
                                                    <p className="text-xs text-muted-foreground mt-0.5">
                                                        Para: <strong>{log.to_email}</strong>
                                                        {log.cc_email && <> · CC: {log.cc_email}</>}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-3 mt-2">
                                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <PiCalendarBold className="h-3 w-3" />
                                                            {log.sent_at ? new Date(log.sent_at).toLocaleString('pt-BR') : '—'}
                                                        </span>
                                                        {log.user_name && (
                                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                <PiUserBold className="h-3 w-3" />
                                                                {log.user_name}
                                                            </span>
                                                        )}
                                                        {log.shipment_number && (
                                                            <Link to={`/shipments/${log.shipment_id}`} className="text-xs text-primary hover:underline flex items-center gap-1">
                                                                <PiLinkBold className="h-3 w-3" />
                                                                #{log.shipment_number}
                                                            </Link>
                                                        )}
                                                    </div>
                                                    {log.body && (
                                                        <div className="mt-3 rounded-lg bg-muted/30 p-3">
                                                            <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{log.body}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => deleteEmail.mutate(log.id)}
                                                    disabled={deleteEmail.isPending}
                                                    className="p-2 rounded-lg text-muted-foreground hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 cursor-pointer shrink-0"
                                                    title="Remover"
                                                >
                                                    <PiTrashBold className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}
